import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from 'styled-components';
import { CUSTOMER_INFO_URL } from '../../constants/paths';
import { SearchResultItem } from './search-result-item';
import { useSearchStore } from './use-search-store';
import Scanner from '../../components/Scanner/Scanner';
import { Icon } from '@purpurds/purpur/icon';
import { icons } from 'theme/icons';
import { purpurShadowSm } from '@purpurds/tokens/shadow/variables';
import { purpurColorGray50, purpurColorGray150, purpurColorRed300, } from '@purpurds/tokens/color/variables';
import { useSearchResults } from './api';
import { CheckboxField, PurPurButton, Loader, SearchInputField } from 'components';
import { useNavigate } from 'react-router';
import spacing from 'theme/spacing';
import { pxToRem } from 'theme/utils';
const TEXT = {
    CUSTOMER_SEARCH_HEADING: 'KUNDESØK',
    NO_RESULTS_FOUND: 'Ingen kunder ble funnet. Kontroller at riktig serienummer eller MAC-adresse er oppgitt, eller prøv med et annet av kundens produkter.',
    NO_RESULTS_FOUND_2: 'Om problemet vedvarer, kontakt teknikertelefonen på +47 23 88 99 89.',
    UNEXPECTED_ERROR: 'Uventet feil i svar.',
    LOADING_TEXT: 'Søker etter kunder...',
};
const CustomerSearchFormWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: ${spacing.spacing24};
`;
const CustomerHeading = styled.h1 `
  margin-bottom: ${spacing.spacing16};
  font-weight: 500;
`;
const CustomerAndFormWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${pxToRem(370)};

  @media (max-width: 768px) {
    width: auto;
    flex-direction: column;
  }
`;
const SearchFormWrapper = styled.div `
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin: ${spacing.spacing8} 0 ${spacing.spacing16};
  padding-left: 0.5rem;
  box-sizing: border-box;
  gap: 0.5rem;
`;
const CheckboxWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  font-weight: 500;
  font-size: 16px;
`;
const CheckboxAndButtonWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;
const CustomerSearchResultsWrapper = styled.div `
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;
const ResultsWrapper = styled.div `
  background-color: ${purpurColorGray50};
  border-radius: 0.333333rem;
  box-shadow: ${purpurShadowSm};
  display: flex;
  -webkit-box-flex: 1;
  flex-grow: 1;
  overflow: hidden;
`;
const StyledScrollWrapper = styled.div `
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  position: relative;

  ::-webkit-scrollbar {
    background: transparent;
    width: 5px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: rgb(220, 220, 225);
    border-radius: 5px;
    outline: none;
    width: 5px;
  }
`;
const Error = styled.div `
  display: flex;
  align-items: flex-start;
  text-align: center;
  font-size: 18px;
  justify-content: center;
  border-radius: 8px;
  border-width: 0.4rem 0.13rem 0.13rem;
  border-style: solid;
  border-color: ${purpurColorRed300};
  margin: ${spacing.spacing16} ${spacing.spacing12} 0;
  padding: ${spacing.spacing16};
  width: 100%;
`;
const ErrorText = styled.div `
  margin-left: ${spacing.spacing8};
`;
const ResultListItem = styled.li `
  display: flex;
  &:nth-child(odd) {
    background-color: ${purpurColorGray150};
  }
`;
const IconWrapper = styled.div `
  width: 30px;
`;
export const CustomerSearchForm = () => {
    const navigate = useNavigate();
    const [showScanner, setShowScanner] = useState(false);
    const { searchInput, setSearchInput, searchQuery, setSearchQuery, onlyActive, setSearchOnlyActive, cache, setCache, cacheKey, setCacheKey, } = useSearchStore();
    const { searchResults, isLoading, isFetching, isError, refetch } = useSearchResults(searchQuery, onlyActive);
    const onSearchClicked = () => {
        if (searchInput !== searchQuery) {
            setSearchQuery(searchInput);
        }
        else {
            setCacheKey('');
            setCache(null);
            setTimeout(() => {
                void refetch();
            }, 1);
        }
    };
    const onScannerDetected = result => {
        setSearchInput(result.codeResult.code);
        setSearchQuery(result.codeResult.code);
        setShowScanner(false);
        onSearchClicked();
    };
    return (_jsxs(CustomerSearchFormWrapper, { children: [_jsx(CustomerHeading, { children: TEXT.CUSTOMER_SEARCH_HEADING }), _jsxs(CustomerAndFormWrapper, { children: [_jsxs(SearchFormWrapper, { children: [_jsx(SearchInputField, { clearButtonAriaLabel: "Clear", onChange: e => setSearchInput(e.target.value), onClear: () => setSearchInput(''), onSearch: e => {
                                    e.preventDefault();
                                    onSearchClicked();
                                }, value: searchInput, variant: "button-attached", placeholder: "S\u00F8k...", iconOnlySearchButton: true }), _jsxs(CheckboxAndButtonWrapper, { children: [searchResults && (_jsx(CheckboxWrapper, { children: _jsx(CheckboxField, { id: "checkbox-active", label: "Active", checked: onlyActive, onChange: () => setSearchOnlyActive(!onlyActive) }) })), _jsx(PurPurButton, { size: "sm", type: "button", variant: "tertiary-purple", onClick: () => setShowScanner(!showScanner), iconOnly: true, children: _jsx(Icon, { svg: icons.scan, size: "md" }) })] })] }), (isFetching || isLoading) && searchQuery.trim() !== '' ? (_jsx(Loader, { loaderText: TEXT.LOADING_TEXT })) : (_jsxs(CustomerSearchResultsWrapper, { children: [searchResults && !!searchResults.customers.length && (_jsx(ResultsWrapper, { children: _jsx(StyledScrollWrapper, { as: "ul", children: searchResults.customers.map(customer => (_jsx(ResultListItem, { children: _jsx(SearchResultItem, { customer: customer, onClick: () => navigate(CUSTOMER_INFO_URL(customer.customerId)) }) }, customer.customerId))) }) })), showScanner && (_jsx(Scanner, { mainHeading: "Skanning av Serienummer", onHeaderClick: () => setShowScanner(false), onDetected: onScannerDetected })), searchResults && searchResults.customers.length === 0 && (_jsxs(Error, { children: [_jsx(IconWrapper, { children: _jsx(Icon, { svg: icons.warning, size: "md", style: { color: purpurColorRed300 } }) }), _jsxs(ErrorText, { children: [TEXT.NO_RESULTS_FOUND, " ", _jsx("br", {}), TEXT.NO_RESULTS_FOUND_2] })] })), isError && (_jsxs(Error, { children: [_jsx(Icon, { svg: icons.error, size: "md", style: { color: purpurColorRed300 } }), _jsx(ErrorText, { children: TEXT.UNEXPECTED_ERROR })] }))] }))] })] }));
};
